import { Image, ImageOff, Info, Loader2 } from "lucide-react"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { TaskAttributes } from "types/api/tasks"
import { Button, buttonVariants, DropdownMenu, Skeleton } from "ui"
import { cn } from "utils/cn"
import { isInChromeExtension } from "utils/proxyUtils"
import { getPendingTask } from "../../../../../store/tasksSlice"
import { getIsScreenshotInProgress, setLightboxImageUrl, setLightboxOpen } from "../../../../../store/uiSlice"
import { Lens } from "../../../lens"

interface Props {
    taskDetails?: Partial<TaskAttributes>
    isPendingTask?: boolean
    openLightbox?: boolean
}

export const Screenshot = ({ taskDetails, isPendingTask }: Props) => {
    const { t } = useTranslation()
    const dispatch = useDispatch();
    const [hovering, setHovering] = useState(false);
    const [imageError, setImageError] = useState(false);
    const screenShotInProgress = useSelector(getIsScreenshotInProgress);
    const pendingTask = useSelector(getPendingTask)

    const pendingScreenshot = pendingTask?.screenshot;
    const taskScreenshot = taskDetails?.wpf_task_screenshot;
    const hasScreenshot = isPendingTask ? !!pendingScreenshot : !!taskScreenshot;
    const [readyScreenshot, setReadyScreenshot] = useState<string | null>(null);
    const [loadingImage, setLoadingImage] = useState(false);

    const handleImageError = () => {
        setImageError(true);
    };


    const waitForImage = async (url: string, maxAttempts = 5, delay = 2000) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                // Use an img element to check if the image loads (avoids CORS issues)
                const img = new window.Image();
                const loadPromise = new Promise<boolean>((resolve) => {
                    img.onload = () => resolve(true);
                    img.onerror = () => resolve(false);
                });
                img.src = url;
                const loaded = await loadPromise;
                if (loaded) return true;
            } catch (_e) {
                // ignore errors
            }
            await new Promise((res) => setTimeout(res, delay));
        }
        return false;
    };

    useEffect(() => {
        const url = isPendingTask ? pendingScreenshot : taskScreenshot;
        if (!url) {
            setReadyScreenshot(null);
            setLoadingImage(false);
            return;
        }

        setLoadingImage(true);
        let cancelled = false;

        (async () => {
            const exists = await waitForImage(url);
            if (!cancelled) {
                if (exists) {
                    setReadyScreenshot(url);
                }
                setLoadingImage(false);
            }
        })();

        return () => { cancelled = true };
    }, [pendingScreenshot, taskScreenshot, isPendingTask]);


    return (
        <DropdownMenu modal={false}>
            <DropdownMenu.Trigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t('screenshot.tooltip', 'Snapshot of what was seen when the task was made')}
                    toolTipPortal
                    loading={screenShotInProgress && !hasScreenshot}
                    disabled={!hasScreenshot}
                >
                    <Image size={18} aria-hidden="true" />
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
                portal={true}
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                align="start"
                className="flex flex-col gap-2 p-1 rounded-xl w-96 pointer-events-auto">
                <div>
                    <AnimatePresence mode="wait">
                        {loadingImage ? (
                            <motion.div
                                key="loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="p-4 items-center flex aspect-video justify-center text-center text-textcolor bg-gray-100 dark:bg-card rounded-lg text-sm"
                            >
                                <Skeleton className="flex gap-2 mx-auto ">
                                    <Loader2 size={18} className="animate-rotate-360 repeat-infinite" />{t('screenshot.loading', 'Loading screenshot')}
                                </Skeleton>
                            </motion.div>
                        ) : !readyScreenshot || imageError ? (
                            <motion.div
                                key="error"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="p-4 items-center flex gap-2 mx-auto aspect-video justify-center text-center text-textcolor bg-gray-100 dark:bg-card rounded-lg text-sm"
                            >
                                <ImageOff size={18} /> {t('screenshot.not_available', 'Screenshot not available')}
                            </motion.div>
                        ) : (
                            <motion.div
                                className="rounded-lg h-[210px] overflow-y-auto"
                                data-scrollable
                                onWheel={(e) => e.stopPropagation()}
                                style={{ WebkitOverflowScrolling: 'touch' }}
                                key="image"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Lens hovering={hovering} setHovering={setHovering}>
                                    <img
                                        className="cursor-pointer"
                                        onClick={() => {
                                            dispatch(setLightboxImageUrl(readyScreenshot));
                                            dispatch(setLightboxOpen(true));
                                        }}
                                        src={readyScreenshot}
                                        alt={t('screenshot.task_alt', 'Task screenshot')}
                                        onError={handleImageError}
                                    />
                                </Lens>
                            </motion.div>

                        )}
                    </AnimatePresence>
                    {!isInChromeExtension() && (
                        <div className="flex items-center gap-1 text-textcolor justify-center my-1">
                            <Info size={14} />
                            <p className="text-xs">
                                {t('screenshot.info', 'Not very accurate? Fix with our ')}
                                <a href='https://chromewebstore.google.com/detail/atarim-visual-website-col/ekpmbeilnaahlccpgjchjjoonecikehf'
                                    target="_blank"
                                    className={cn(
                                        buttonVariants({
                                            variant: 'link',
                                            size: 'link',
                                        }),
                                        'text-xs font-normal'
                                    )}>{t('chrome_extension', 'Chrome Extension')}</a>
                            </p>
                        </div>
                    )}
                </div>
            </DropdownMenu.Content>
        </DropdownMenu>
    )
}